/**
 * The words of the context bar (Phase B step 3, docs/view-context.md): every
 * chip states its value in plain words with dates written out, filter chips
 * summarise the selection, and each screen states how it counts. Pure: the
 * bar, the Filters panel and maître d'AI's compact context line all read
 * their text from here, so the three never say different things.
 */
import i18n, { currentLocale } from '../../../i18n';
import { dateLocale } from '../../../lib/localePrefs';
import { sourceMeta } from '../data/reservationSources';
import type { PerfSettings, ReservationSourceId, ReservationStatusGroup } from '../types';
import { RESERVATION_STATUS_GROUPS } from '../types';
import { effectiveCompareKind, frameLabel, isDateRange } from './perfSettings';
import {
  localToday,
  PERF_COMPARE_KIND_OF,
  screenCapabilities,
  type ContextPart,
  type RequestViewContext,
  type ScreenId,
  type ViewContextComparison,
  type ViewContextPeriod,
} from './viewContext';

const t = (key: string, opts?: Record<string, unknown>) => i18n.t(`chrome:context.${key}`, opts) as string;

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

function utcNoon(iso: string): Date {
  return new Date(`${iso}T12:00:00Z`);
}

function locale(): string {
  return dateLocale(currentLocale());
}

/** "14 Sept 2026" (en) / "14. Sept. 2026" (de). */
export function formatDay(iso: string, opts: { year?: boolean; weekday?: boolean } = {}): string {
  if (!iso) return '';
  const d = utcNoon(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat(locale(), {
    timeZone: 'UTC',
    day: 'numeric',
    month: 'short',
    ...(opts.year === false ? {} : { year: 'numeric' }),
    ...(opts.weekday ? { weekday: 'short' } : {}),
  }).format(d);
}

/** "14 Sept – 11 Oct 2026", "1–28 Sept 2026", "15 Dec 2025 – 11 Jan 2026". */
export function formatRange(start: string | null | undefined, end: string | null | undefined): string {
  if (!start || !end) return '';
  const a = utcNoon(start);
  const b = utcNoon(end);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return `${start} – ${end}`;
  const fmt = new Intl.DateTimeFormat(locale(), { timeZone: 'UTC', day: 'numeric', month: 'short', year: 'numeric' });
  if (start === end) return fmt.format(a);
  const range = (fmt as Intl.DateTimeFormat & { formatRange?: (x: Date, y: Date) => string }).formatRange;
  if (typeof range === 'function') {
    // Spaced en dash between the two ends, as the rest of Insights writes it.
    return range.call(fmt, a, b).replace(/\s*[–-]\s*/, ' – ');
  }
  return `${fmt.format(a)} – ${fmt.format(b)}`;
}

// ---------------------------------------------------------------------------
// Screens
// ---------------------------------------------------------------------------

export type ScreenFamily = 'assistant' | 'performance' | 'live' | 'forecast' | 'settings' | 'growth';

export function screenFamily(screen: ScreenId): ScreenFamily {
  if (screen.startsWith('growth:')) return 'growth';
  if (screen === 'performance-drill') return 'performance';
  return screen as ScreenFamily;
}

/** "Forecast", "Growth", "maître d'AI": the screen's family, by name. */
export function screenLabel(screen: ScreenId): string {
  return t(`screens.${screenFamily(screen)}`);
}

/**
 * Growth's sub-screens that ignore some of the context name the place the
 * person is on, not Growth, which uses every part: the connect wizard
 * ("while connecting Google"), Data health with its connections and
 * settings, and Campaigns with its editor. Keys under `context.notUsedAt`.
 */
const NOT_USED_PLACE: Partial<Record<ScreenId, string>> = {
  'growth:setup': 'growthSetup',
  'growth:health': 'growthHealth',
  'growth:campaigns': 'growthCampaigns',
  'growth:accounts': 'growthAccounts',
};

/** What a muted chip adds: "not used on Forecast", "not used while connecting Google". */
export function notUsedNote(screen: ScreenId): string {
  const place = NOT_USED_PLACE[screen];
  return place ? t(`notUsedAt.${place}`) : t('notUsed', { screen: screenLabel(screen) });
}

/** A muted chip's hover text: the part, where it isn't used, and that it still applies elsewhere. */
export function notUsedTitle(screen: ScreenId, value: string): string {
  return t('notUsedTitle', { value, note: notUsedNote(screen) });
}

/** The Filters panel's note on a part the screen doesn't use, as a sentence. */
export function notUsedPanelNote(screen: ScreenId): string {
  const note = notUsedNote(screen);
  return t('panel.notUsedNote', { note: note.charAt(0).toLocaleUpperCase() + note.slice(1) });
}

// ---------------------------------------------------------------------------
// Period and comparison
// ---------------------------------------------------------------------------

export type PeriodSummary = {
  /** "14 Sept – 11 Oct 2026". */
  range: string;
  /** "Last 4 weeks", or "Custom dates" for a range. */
  name: string;
  inProgress: boolean;
};

export function periodSummary(period: ViewContextPeriod, settings: PerfSettings): PeriodSummary {
  return {
    range: formatRange(period.start, period.end),
    name: isDateRange(settings) ? t('period.customDates') : frameLabel(settings),
    inProgress: period.inProgress,
  };
}

export type ComparisonSummary = {
  /** "vs 15 Sept – 12 Oct 2025" or "No comparison". */
  text: string;
  /** "same weekdays", "same dates", "previous period", "custom dates"; empty for none. */
  kind: string;
  none: boolean;
};

export function comparisonSummary(comparison: ViewContextComparison, settings: PerfSettings): ComparisonSummary {
  const kind = effectiveCompareKind(settings);
  if (comparison.kind === 'none' || !comparison.start || !comparison.end) {
    return { text: t('comparison.none'), kind: '', none: true };
  }
  return {
    text: t('comparison.vs', { range: formatRange(comparison.start, comparison.end) }),
    kind: t(`comparison.kind.${kind}`),
    none: false,
  };
}

// ---------------------------------------------------------------------------
// Statuses, sources, shifts
// ---------------------------------------------------------------------------

/** Reservations first, then what never became one: the order statuses are written in. */
const STATUS_WORD_ORDER: ReservationStatusGroup[] = ['confirmed', 'seated', 'done', 'cancelled', 'noshow', 'rejected', 'waitlist'];

/** Statuses in running text: "no-shows, rejected requests and the waitlist". */
export function statusWords(statuses: readonly ReservationStatusGroup[]): string {
  const set = new Set(statuses);
  return listJoin(STATUS_WORD_ORDER.filter(id => set.has(id)).map(id => t(`statusWords.${id}`)));
}

export function statusLabel(id: ReservationStatusGroup): string {
  return RESERVATION_STATUS_GROUPS.find(g => g.id === id)?.label ?? id;
}

/** "Done", "Done, Seated", "Done, Seated +2": a list that stays one chip. */
export function shortList(names: string[], keep = 2): string {
  if (names.length <= keep + 1) return names.join(', ');
  return `${names.slice(0, keep).join(', ')} ${t('more', { count: names.length - keep })}`;
}

function orderedStatuses(statuses: readonly ReservationStatusGroup[]): ReservationStatusGroup[] {
  const set = new Set(statuses);
  return RESERVATION_STATUS_GROUPS.map(g => g.id).filter(id => set.has(id));
}

export function statusesText(statuses: readonly ReservationStatusGroup[]): string {
  if (!statuses.length) return t('filters.allStatuses');
  return t('filters.statuses', { list: shortList(orderedStatuses(statuses).map(statusLabel)) });
}

export function sourcesText(sources: readonly ReservationSourceId[]): string {
  if (!sources.length) return t('filters.allSources');
  return t('filters.sources', { list: shortList(sources.map(id => sourceMeta(id).label)) });
}

export function shiftsText(shifts: readonly string[]): string {
  if (!shifts.length) return t('filters.allShifts');
  return t('filters.shifts', { list: shortList([...shifts]) });
}

type FilterState = {
  status: readonly ReservationStatusGroup[];
  source: readonly ReservationSourceId[];
  shifts: readonly string[];
};

/** The text of one context part's current value, for chips and the panel's section list. */
export function partValueText(
  part: Exclude<ContextPart, 'venues'>,
  state: FilterState & { period?: PeriodSummary; comparison?: ComparisonSummary },
): string {
  switch (part) {
    case 'statuses':
      return statusesText(state.status);
    case 'sources':
      return sourcesText(state.source);
    case 'shifts':
      return shiftsText(state.shifts);
    case 'period':
      return state.period ? `${state.period.name} · ${state.period.range}` : '';
    case 'comparison':
      return state.comparison
        ? state.comparison.none
          ? state.comparison.text
          : `${state.comparison.text} · ${state.comparison.kind}`
        : '';
    default:
      return '';
  }
}

/**
 * The reservation filters the screen applies, as one chip: "All statuses ·
 * all sources · all shifts" or "Statuses: Done · Sources: Widget · all
 * shifts". Null when the screen applies none (Settings, Campaigns).
 */
export function filtersChipText(
  screen: ScreenId,
  state: FilterState,
  uses: Record<ContextPart, boolean> = screenCapabilities(screen).uses,
): string | null {
  const parts: string[] = [];
  if (uses.statuses) parts.push(statusesText(state.status));
  if (uses.sources) parts.push(sourcesText(state.source));
  if (uses.shifts) parts.push(shiftsText(state.shifts));
  if (!parts.length) return null;
  // Lower-case the "all …" parts after the first, so the chip reads as one sentence.
  return parts.map((part, i) => (i > 0 && isAllText(part) ? lowerFirst(part) : part)).join(' · ');
}

function isAllText(text: string): boolean {
  return [t('filters.allStatuses'), t('filters.allSources'), t('filters.allShifts')].includes(text);
}

function lowerFirst(text: string): string {
  // German nouns keep their capital ("alle Quellen" is already lower-case in the catalog).
  return currentLocale() === 'de' ? text.replace(/^Alle /, 'alle ') : text.charAt(0).toLowerCase() + text.slice(1);
}

// ---------------------------------------------------------------------------
// Growth's outcomes under a status filter (decision 7.5.2)
// ---------------------------------------------------------------------------

export type GrowthOutcome = 'completed' | 'cancelled' | 'noShow';

export type GrowthStatusEffects = {
  /** Chosen statuses Growth never counts: the waitlist and rejected requests. */
  notCounted: ReservationStatusGroup[];
  /** Outcomes the status filter leaves out; Growth shows them as excluded, never 0. */
  excluded: GrowthOutcome[];
  /** The statuses chosen match no outcome at all. */
  matchesNothing: boolean;
};

const COMPLETED: ReservationStatusGroup[] = ['confirmed', 'seated', 'done'];

export function growthStatusEffects(statuses: readonly ReservationStatusGroup[]): GrowthStatusEffects {
  if (!statuses.length) return { notCounted: [], excluded: [], matchesNothing: false };
  const set = new Set(statuses);
  const notCounted = orderedStatuses(statuses).filter(id => id === 'waitlist' || id === 'rejected');
  const excluded: GrowthOutcome[] = [];
  if (!COMPLETED.some(id => set.has(id))) excluded.push('completed');
  if (!set.has('cancelled')) excluded.push('cancelled');
  if (!set.has('noshow')) excluded.push('noShow');
  return { notCounted, excluded, matchesNothing: excluded.length === 3 };
}

// ---------------------------------------------------------------------------
// Definitions: how each screen counts
// ---------------------------------------------------------------------------

/**
 * How a screen counts, as one key. maître d'AI's receipt carries the same
 * keys (backend answer_receipt._definition), so the receipt's "How it
 * counts" and the bar's definitions chip say the same thing in the same
 * words: `definitionChip` is the one source of both.
 */
export type DefinitionKey =
  | 'every-status'
  | 'statuses-chosen'
  | 'growth-outcomes'
  | 'growth-campaigns'
  | 'growth-health'
  | 'growth-setup'
  | 'forecast-booked'
  | 'live-today'
  | 'settings-setup'
  | 'assistant-context';

export type Definitions = {
  /** The key the chip's words come from (definitionChip). */
  key: DefinitionKey;
  /** The chip, e.g. "Growth counts by outcome". */
  chip: string;
  /** The popover's heading. */
  title: string;
  lines: string[];
  /**
   * Status changes the popover offers as buttons (Performance only):
   * `every` clears the statuses.
   */
  actions?: DefinitionAction[];
};

export type DefinitionAction = 'every';

/** The statuses a definitions button applies: `every` clears them. */
export function definitionActionStatuses(action: DefinitionAction): ReservationStatusGroup[] {
  switch (action) {
    case 'every':
      return [];
  }
}

/** "completed, cancellations and no-shows" / "… und No-Shows". */
export function listJoin(items: string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} ${t('and')} ${items[items.length - 1]}`;
}

/** Performance's key for its statuses: every status, or the statuses chosen. */
export function statusDefinitionKey(statuses: readonly ReservationStatusGroup[]): DefinitionKey {
  return statuses.length ? 'statuses-chosen' : 'every-status';
}

/** The key of a screen's definitions chip under these statuses. */
export function definitionKeyFor(screen: ScreenId, statuses: readonly ReservationStatusGroup[]): DefinitionKey {
  const family = screenFamily(screen);
  if (family === 'growth') {
    if (screen === 'growth:campaigns') return 'growth-campaigns';
    if (screen === 'growth:health') return 'growth-health';
    if (screen === 'growth:setup') return 'growth-setup';
    return 'growth-outcomes';
  }
  if (family === 'performance') return statusDefinitionKey(statuses);
  if (family === 'forecast') return 'forecast-booked';
  if (family === 'live') return 'live-today';
  if (family === 'settings') return 'settings-setup';
  return 'assistant-context';
}

/**
 * The chip's words for a definition key, in the person's language: the
 * context bar and every answer's receipt read them from here. Growth's names
 * the outcomes the statuses leave out ("· cancellations and no-shows
 * excluded"). Null for a key no screen states (the receipt's own, e.g. POS
 * revenue by payment date).
 */
export function definitionChip(key: string, statuses: readonly ReservationStatusGroup[] = []): string | null {
  switch (key) {
    case 'every-status':
      return t('definitions.performance.chipAll');
    // A receipt saved before the old status preset was removed says what
    // was chosen, as any selection does.
    case 'reporting-default':
    case 'statuses-chosen':
      return t('definitions.performance.chipChosen');
    case 'growth-outcomes': {
      const effects = growthStatusEffects(statuses);
      if (effects.matchesNothing) return t('definitions.growth.chipNothing');
      if (effects.excluded.length) {
        const outcomes = listJoin(effects.excluded.map(o => t(`definitions.growth.outcome.${o}`)));
        return t('definitions.growth.chipExcluded', { outcomes });
      }
      return t('definitions.growth.chip');
    }
    case 'growth-campaigns':
      return t('definitions.campaigns.chip');
    case 'growth-health':
      return t('definitions.health.chip');
    case 'growth-setup':
      return t('definitions.setup.chip');
    case 'forecast-booked':
      return t('definitions.forecast.chip');
    case 'live-today':
      return t('definitions.live.chip');
    case 'settings-setup':
      return t('definitions.settings.chip');
    case 'assistant-context':
      return t('definitions.assistant.chip');
    default:
      return null;
  }
}

export function definitionsFor(screen: ScreenId, state: FilterState): Definitions {
  const family = screenFamily(screen);
  const key = definitionKeyFor(screen, state.status);
  const chip = definitionChip(key, state.status) ?? '';
  if (family === 'growth') {
    if (screen === 'growth:campaigns') {
      return { key, chip, title: t('definitions.campaigns.title'), lines: [t('definitions.campaigns.line1')] };
    }
    if (screen === 'growth:health') {
      return { key, chip, title: t('definitions.health.title'), lines: [t('definitions.health.line1')] };
    }
    if (screen === 'growth:setup') {
      return { key, chip, title: t('definitions.setup.title'), lines: [t('definitions.setup.line1')] };
    }
    const effects = growthStatusEffects(state.status);
    const lines = [t('definitions.growth.line1'), t('definitions.growth.line2'), t('definitions.growth.line3')];
    if (effects.matchesNothing) {
      lines.unshift(t('definitions.growth.matchesNothing'));
    } else {
      if (effects.excluded.length) {
        const outcomes = listJoin(effects.excluded.map(o => t(`definitions.growth.outcome.${o}`)));
        lines.unshift(t('definitions.growth.excluded', { outcomes }));
      }
      if (effects.notCounted.length) {
        lines.unshift(t('definitions.growth.notCounted', { statuses: listJoin(effects.notCounted.map(statusLabel)) }));
      }
    }
    return { key, chip, title: t('definitions.growth.title'), lines };
  }
  if (family === 'performance') {
    // What is counted now, and the one change that makes sense from here.
    const title = t('definitions.performance.title');
    if (key === 'every-status') {
      return { key, chip, title, lines: [t('definitions.performance.all'), t('definitions.performance.rates')] };
    }
    return {
      key,
      chip,
      title,
      lines: [
        t('definitions.performance.chosen', { list: statusWords(state.status) }),
        t('definitions.performance.ratesChosen'),
      ],
      actions: ['every'],
    };
  }
  return {
    key,
    chip,
    title: t(`definitions.${family}.title`),
    lines: [t(`definitions.${family}.line1`), t(`definitions.${family}.line2`)],
  };
}

// ---------------------------------------------------------------------------
// A whole context in one line (maître d'AI's panel, a conversation's row)
// ---------------------------------------------------------------------------

/** "vs 15 Sept – 12 Oct 2025 · same weekdays", or "No comparison", from a context alone. */
export function comparisonText(comparison: ViewContextComparison): string {
  if (comparison.kind === 'none' || !comparison.start || !comparison.end) return t('comparison.none');
  const kind = t(`comparison.kind.${PERF_COMPARE_KIND_OF[comparison.kind]}`);
  const vs = t('comparison.vs', { range: formatRange(comparison.start, comparison.end) });
  return kind ? `${vs} · ${kind}` : vs;
}

/** "Next 30 days · 16 Oct – 14 Nov 2026" or "Today · Fri 16 Oct": a screen's own window. */
export function ownWindowText(window: RequestViewContext['window']): string | null {
  if (!window) return null;
  if (window.kind === 'live') {
    return t('own.live', { date: formatDay(window.start ?? localToday(), { weekday: true, year: false }) });
  }
  // The window's name in the person's language from its key ("next30"), not
  // the words it was recorded with.
  const days = /^next(\d+)$/.exec(window.key ?? '')?.[1];
  const label = days ? t('own.forecastNext', { count: Number(days) }) : (window.label ?? '');
  if (!window.start || !window.end) return label || null;
  return t('own.forecast', { label, range: formatRange(window.start, window.end) }).replace(/^ · /, '');
}

export type ContextLinePart = { key: string; text: string; icon?: string; muted?: boolean };

/** The venues of a context in the bar's words: "Linden Group · 3 venues". */
export function venuesText(vc: Pick<RequestViewContext, 'venues'>): string {
  const count = vc.venues.ids.length;
  return count > 1 ? `${vc.venues.label} · ${t('venues.count', { count })}` : vc.venues.label;
}

/**
 * One context as the compact line reads it: the venues, a local focus, the
 * screen's own window, the period and comparison it uses, the reservation
 * filters it applies and how it counts. The screen's context line and a
 * conversation's row both read it, so they say the same things.
 */
export function contextLineParts(vc: RequestViewContext): { parts: ContextLinePart[]; definitions: Definitions } {
  const uses = vc.uses ?? screenCapabilities(vc.screen).uses;
  const parts: ContextLinePart[] = [{ key: 'venues', text: venuesText(vc), icon: 'utensils' }];
  if (vc.focus) parts.push({ key: 'focus', text: `› ${vc.focus.label}` });
  const own = ownWindowText(vc.window);
  if (own) parts.push({ key: 'own', text: own, icon: 'calendar' });
  if (uses.period && vc.period?.start && vc.period?.end) {
    parts.push({ key: 'period', text: formatRange(vc.period.start, vc.period.end), icon: own ? undefined : 'calendar' });
  }
  if (uses.comparison && vc.comparison) parts.push({ key: 'comparison', text: comparisonText(vc.comparison) });
  const state = { status: vc.filters.statuses, source: vc.filters.sources, shifts: vc.filters.shifts };
  const filters = filtersChipText(vc.screen, state, uses);
  if (filters) parts.push({ key: 'filters', text: filters, icon: 'sliders' });
  // A filter set but not used on the context's screen stays visible, muted,
  // as in the bar ("Statuses: Done · not used on Forecast").
  const unused = [
    !uses.statuses && state.status.length ? statusesText(state.status) : null,
    !uses.sources && state.source.length ? sourcesText(state.source) : null,
    !uses.shifts && state.shifts.length ? shiftsText(state.shifts) : null,
  ].filter((text): text is string => Boolean(text));
  for (const text of unused) {
    parts.push({ key: `unused-${text}`, text: `${text} · ${notUsedNote(vc.screen)}`, muted: true });
  }
  return { parts, definitions: definitionsFor(vc.screen, state) };
}
